import { Pipe, PipeTransform } from '@angular/core'
import { DATE_FORMATS } from '../../shared-components/constants'

@Pipe({
  name: 'transformDateTime',
  standalone: false,
})
export class TransformDateTimePipe implements PipeTransform {
  transform(value: any, type = 'date', source: string = ''): string {
    if (!value) {
      return ''
    }

    // Handle different input types
    let date: Date

    if (value instanceof Date) {
      date = value
    } else if (typeof value === 'string' || typeof value === 'number') {
      if (source === 'fileName') {
        value = this.formatDateString(value.toString())
      }
      date = new Date(value)
    } else {
      return ''
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return ''
    }

    // Format the date based on type
    switch (type.toLowerCase()) {
      case 'date':
        return this.formatDate(date, DATE_FORMATS.DATE)

      case 'time':
        return this.formatTime(date, DATE_FORMATS.TIME)

      case 'datetime':
        return this.formatDateTime(date, DATE_FORMATS.DATE_TIME)

      case 'shortdate': {
        const localDate = this.parseUTCDateTime(value)
        return this.formatDate(localDate, DATE_FORMATS.SHORT_DATE)
      }

      case 'localtime': {
        const localTime = this.parseUTCDateTime(value)
        const timeResult = this.formatTime(localTime, DATE_FORMATS.TIME)
        return timeResult
      }

      case 'localdate': {
        // Convert UTC datetime to local date
        const localDate = this.parseUTCDateTime(value)
        return this.formatDate(localDate, DATE_FORMATS.DATE)
      }

      case 'localdatetime': {
        // Convert UTC datetime to local datetime
        const localDateTime = this.parseUTCDateTime(value)
        return this.formatDateTime(localDateTime, DATE_FORMATS.DATE_TIME)
      }

      default:
        // Default to date format
        return this.formatDate(date, DATE_FORMATS.DATE)
    }
  }

  private formatDate(date: Date, format: string): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const year = date.getFullYear()

    switch (format) {
      case DATE_FORMATS.DATE:
        return `${month}/${day}/${year}`
      case DATE_FORMATS.SHORT_DATE: {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ]
        return `${monthNames[date.getMonth()]} ${day}`
      }
      default:
        return `${month}/${day}/${year}`
    }
  }

  private formatTime(date: Date, format: string): string {
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12

    switch (format) {
      case DATE_FORMATS.TIME:
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`
      default:
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`
    }
  }

  private formatDateTime(date: Date, format: string): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const year = date.getFullYear()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12

    switch (format) {
      case DATE_FORMATS.DATE_TIME:
        return `${month}/${day}/${year}, ${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`
      default:
        return `${month}/${day}/${year}, ${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`
    }
  }

  /**
   * Parse UTC datetime string and convert to local timezone
   * Handles formats like "2025-12-19T06:53:56.479117 (UTC)" or "2025-12-19T06:53:56.479117"
   */
  private parseUTCDateTime(value: any): Date {
    if (!value) {
      return new Date()
    }

    let dateString = value.toString().trim()

    // Remove "(UTC)" if present
    dateString = dateString.replace(/\s*\(UTC\)\s*$/i, '')

    /**
     * Check for timezone:
     *  - Ends with Z
     *  - OR has +HH:mm or -HH:mm at the end
     */
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(dateString)

    // If no timezone info, assume UTC
    if (!hasTimezone) {
      dateString += 'Z'
    }

    const parsedDate = new Date(dateString)

    if (isNaN(parsedDate.getTime())) {
      console.warn('Unable to parse datetime:', value)
      return new Date()
    }

    return parsedDate
  }
  formatDateString(event: string): string {
    const date = event.substring(0, 19)
    const year = date.substring(0, 4)
    const month = date.substring(4, 6)
    const day = date.substring(6, 8)
    return `${year}/${month}/${day}`
  }
}
